/**
 * Sets body overflow and position styles to prevent/allow scrolling
 * Uses reference counting to handle multiple components requesting locks
 *
 * @param isOpen - Whether to prevent scrolling (true) or allow it (false)
 * @param id - Unique ID for this caller
 *
 * @remarks
 * This function uses a reference counting system with component IDs:
 * - When isOpen=true: Adds a lock for this component ID
 * - When isOpen=false: Removes the lock for this component ID
 * - Body scroll is only locked when at least one component has a lock
 * - Body scroll is only unlocked when all components have released their locks
 *
 * The ID prevents:
 * - Duplicate locks from the same component
 * - Invalid unlocks from components that didn't add a lock
 * - Race conditions between multiple components
 *
 * @example
 * ```tsx
 * // In a modal or slide-over component
 * useEffect(() => {
 *   if (isOpen) {
 *     setBodyOverflow(true, 'cart')
 *   }
 *   return () => setBodyOverflow(false, 'cart')
 * }, [isOpen])
 * ```
 */

// Reference counter - tracks how many components want scroll locked
let scrollLockCount = 0
// Track which components have locks (prevents duplicate locks and invalid unlocks)
const activeLocks = new Set<string>()

export const setBodyOverflow = (
  isOpen: boolean,
  options?: {
    id?: string
    noFixed?: boolean
  }
): void => {
  if (typeof document === "undefined") return

  const componentId = options?.id || "unknown"

  if (isOpen) {
    // Add lock
    if (!activeLocks.has(componentId)) {
      scrollLockCount++
      activeLocks.add(componentId)
    }

    // Apply styles only on first lock
    if (scrollLockCount === 1) {
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth

      document.body.style.overflow = "hidden"
      // Add padding to compensate for removed scrollbar
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`
      }
      if (!options?.noFixed) {
        document.body.style.position = "fixed"
      }
    }
  } else {
    // Remove lock
    if (activeLocks.has(componentId)) {
      scrollLockCount = Math.max(0, scrollLockCount - 1)
      activeLocks.delete(componentId)
    } else {
      // Component doesn't have a lock, ignore unlock request
      return
    }

    // Remove styles only when all locks are released
    if (scrollLockCount === 0) {
      document.body.style.overflow = ""
      document.body.style.paddingRight = ""
      if (!options?.noFixed) {
        document.body.style.position = ""
      }
    }
  }
}
